import { useMutation } from "@apollo/client/react";
import { alert } from "@components/common/Alert";
import ExportModal from "@components/email/ExportModal";
import {
  GENERATE_CLIENT_SUMMARY,
  GENERATE_ADAPTIVE_CLIENT_SUMMARY,
} from "@graphql/mutations/ClientSummary";
import EXPORT_CLIENT_SUMMARY_EMAIL from "@graphql/mutations/SendClientSummaryEmail";
import { useStreamResult, useWatchUserWsEvent } from "@hooks";
import { useAutosaveContext } from "@hooks/use-autosave";
import mixpanel from "mixpanel-browser";
import { useState, useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";

import GeneratedSummary from "./GeneratedSummary";
import SelectType from "./SelectType";
import { trackOperation } from "../../../../../utils/operationTracking";

export default function ClientSummary({ note, refetchNote }) {
  //form methods

  const methods = useFormContext();
  const { control, handleSubmit, getValues, setValue } = methods;
  const { setIsStreaming } = useAutosaveContext();

  const [isClientSummaryEditable, setIsClientSummaryEditable] =
    useState(!!note?.clientSummary);

  //queries and mutations
  const [generateSummary, { error: summaryError }] = useMutation(
    GENERATE_CLIENT_SUMMARY,
  );
  const [generateAdaptiveSummary, { error: adaptiveSummaryError }] =
    useMutation(GENERATE_ADAPTIVE_CLIENT_SUMMARY);
  const [exportClientSummaryEmail] = useMutation(
    EXPORT_CLIENT_SUMMARY_EMAIL,
  );

  //state
  const [shouldShowModal, setShouldShowModal] = useState(false);
  const [step, setStep] = useState(note?.clientSummary ? 2 : 1);

  // Reset form fields when modal closes
  useEffect(() => {
    if (!shouldShowModal) {
      setValue("toEmail", "");
      setValue("specialInstructions", "");
      setValue("includeAttachments", true);
    }
  }, [shouldShowModal, setValue]);

  //refs
  const generatedSummaryRef = useRef(null);

  const { connectStream, isStreaming } = useStreamResult();

  useEffect(() => {
    if (note?.clientSummary) {
      setValue("clientSummary", note.clientSummary);
    }
  }, [note?.clientSummary, setValue]);

  const handleAdaptiveClientSummaryComplete = async (message) => {
    const messageNoteUuid = message?.data?.note_uuid;
    if (messageNoteUuid !== note?.uuid) {
      return;
    }

    await trackOperation(
      "Adaptive Client Summary Complete",
      async (ctx) => {
        ctx.addProperties({
          noteUuid: note?.uuid,
          templateUuid: message?.data?.template_uuid,
        });

        const { data } = await refetchNote();
        const updatedNote = data?.note;

        if (updatedNote?.clientSummary) {
          setValue("clientSummary", updatedNote.clientSummary);
        }

        setIsClientSummaryEditable(true);
        setStep(2);
        alert(
          "success",
          "Your adaptive client summary has been generated successfully!",
        );
      },
    );
  };

  useWatchUserWsEvent({
    onMessage: handleAdaptiveClientSummaryComplete,
    eventName: "adaptive_client_summary_complete",
  });

  const handleAdaptiveClientSummaryFailed = async (message) => {
    const messageNoteUuid = message?.data?.note_uuid;
    if (messageNoteUuid !== note?.uuid) {
      return;
    }

    await trackOperation(
      "Adaptive Client Summary Failed",
      async (ctx) => {
        ctx.addProperties({
          noteUuid: note?.uuid,
          templateUuid: message?.data?.template_uuid,
        });

        setIsClientSummaryEditable(true);
        setStep(1);
        alert(
          "error",
          "Failed to generate adaptive client summary. Please try again.",
        );
      },
    );
  };

  useWatchUserWsEvent({
    onMessage: handleAdaptiveClientSummaryFailed,
    eventName: "adaptive_client_summary_failed",
  });

  const handleGenerateDefaultClientSummary = async () => {
    setStep(2);
    let response = await connectStream(
      (message) => {
        const clientSummary = getValues("clientSummary");
        setValue("clientSummary", clientSummary + message);
      },
      () => {
        // use the client summary messages in state to set
        // the form value. Swap out from display only to an editable form field

        setIsClientSummaryEditable(true);
      },
      "Client Summary Generation",
    );

    if (response?.taskUuid === "" || response?.error) {
      alert(
        "error",
        "An error has occurred. Please try again later.",
      );

      setStep(1);
      return;
    }

    const taskUuid = response?.taskUuid;

    setIsClientSummaryEditable(false);
    setIsStreaming(true);
    setValue("clientSummary", "");
    mixpanel.track("Client Summary Generated", {
      noteUuid: note?.uuid,
    });
    generateSummary({
      variables: {
        noteUuid: note?.uuid,
        streamingTaskUuid: taskUuid,
      },
    }).catch((error) => {
      console.error("Error generating client summary:", error);
      mixpanel.track("Client Summary Generation Error", {
        noteUuid: note?.uuid,
        error: error.message,
      });
      alert(
        "error",
        "An error has occurred. Please try again later.",
      );
      setStep(1);
    });
  };

  const handleGenerateAdaptiveClientSummary = async (
    selectedJobType,
  ) => {
    const templateUuid = selectedJobType?.value;

    if (!templateUuid) {
      alert(
        "error",
        "Please select a template to generate a client summary.",
      );
      return;
    }

    setStep(2);
    setIsClientSummaryEditable(false);
    setValue("clientSummary", "");

    await trackOperation(
      "Adaptive Client Summary Generated",
      async (ctx) => {
        ctx.addProperties({
          noteUuid: note?.uuid,
          templateUuid: templateUuid,
        });

        await generateAdaptiveSummary({
          variables: {
            noteUuid: note?.uuid,
            templateUuid: templateUuid,
          },
        });
      },
      () => {
        alert(
          "error",
          "An error has occurred while generating the adaptive client summary. Please try again later.",
        );
        setStep(1);
      },
    );
  };

  const onSend = async (data) => {
    try {
      await exportClientSummaryEmail({
        variables: {
          toEmail: data?.toEmail,
          clientSummary: getValues("clientSummary"),
        },
      });
      const successMessage =
        "Client summary sent to " + data?.toEmail + "!";
      alert("success", successMessage);
      setShouldShowModal(false);
    } catch (error) {
      console.error("Error sending client summary:", error);
    }
  };

  return (
    <>
      <ExportModal
        isClientSummaryMode={true}
        shouldShowModal={shouldShowModal}
        isClientSummaryModalOpen={shouldShowModal}
        hideModal={() => setShouldShowModal(false)}
        handleSubmit={handleSubmit}
        onSend={onSend}
        control={control}
        methods={methods}
        getValues={getValues}
        note={note}
      />
      {step === 1 && (
        <SelectType
          handleGenerateDefaultClientSummary={
            handleGenerateDefaultClientSummary
          }
          handleGenerateAdaptiveClientSummary={
            handleGenerateAdaptiveClientSummary
          }
        />
      )}

      {step === 2 && (
        <GeneratedSummary
          generatedSummaryRef={generatedSummaryRef}
          handleExportClientSummary={() => {
            setShouldShowModal(true);
          }}
          setStep={setStep}
          isClientSummaryEditable={isClientSummaryEditable}
          control={control}
          isStreaming={isStreaming}
          noteUuid={note?.uuid}
        />
      )}

      {(summaryError || adaptiveSummaryError) && (
        <h4 className="mt-10 text-center">
          Error - Please try again later:{" "}
          {summaryError?.message || adaptiveSummaryError?.message}
        </h4>
      )}
    </>
  );
}
